import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery, useMutation } from '@common/hooks';
import { Form, Input, Col, Row, Checkbox, Select, Button, Alert } from 'antd';
import uniqBy from 'lodash/uniqBy';
import { Colors } from '@common/tokens';
import { createMessage } from 'works/legacy/lib/message-helper';
import { allFormErrors } from 'works/legacy/lib/form-helpers';
import {
  Banner,
  Drawer,
  ErrorHandler,
  Icon,
  InfoTooltip,
  LoadingState,
  Modal,
  PercentInput,
  PhoneInput,
} from '@common/components';
import {
  CREATE_WORKER,
  UPDATE_WORKER,
  createWorkerAction,
  expandAllowedUnitsToAllowLists,
  formatAllowListsToAllowedUnits,
  getSelectedHomeUnit,
  updateWorkerAction,
} from './worker-helper';
import { WorkforceGroupSelect } from '@scheduling/components';
import { SEND_APP_INVITATION_MUTATION } from '@scheduling/graphql';
import { RolesQuery } from '@clinician-profile/graphql';

const WORKS_ORGANIZATION_UNIT_QUERY = gql`
  query WorksOrganizationUnits {
    organizationUnits(input: { scope: "organization" }) {
      id
      name
      costCenter
    }
  }
`;

function onFinishModalConfirm(onConfirm) {
  Modal.confirm({
    title: 'Please confirm change in Workforce Group',
    content:
      'Please note, any shifts previously claimed by this Worker will be associated with their prior Workforce Group and will not be impacted by this change.',
    okText: 'Confirm',
    onOk: onConfirm,
  });
}

function confirmWorkforceGroupOverride(conflicts, onConfirm) {
  const suggested = conflicts[0]?.suggestedWorkforceGroup?.name;
  Modal.confirm({
    title: 'Override automatic group mapping?',
    content: suggested
      ? `The automatic mapping rules would place this worker in "${suggested}". Your manual assignment will persist ahead of automatic re-mapping until it is cleared.`
      : 'Your manual assignment differs from the automatic mapping rules and will persist ahead of automatic re-mapping until it is cleared.',
    okText: 'Assign Anyway',
    cancelText: 'Cancel',
    onOk: onConfirm,
  });
}

export function WorkerForm(props) {
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [form] = Form.useForm();
  const worker = props.worker;
  const workerIntegrationEnabled =
    props.workerIntegrationEnabled && props.workerFound;
  const allowListSyncEnabled = props.allowListSyncEnabled && props.workerFound;
  const editingAllowListsPermitted =
    props.editingAllowListsPermitted || !props.workerFound;
  const integrationType = props.integrationType;
  const integrationConfigLoading = props.integrationConfigLoading;
  const isInternal = worker ? worker.isInternal : true;
  const isWorkerActive = worker ? worker.active : true;
  const inactiveText = isWorkerActive ? '' : '(Inactive)';

  const [inputsEnabled, setInputsEnabled] = useState(isWorkerActive);
  const [sendAppInvite, setSendAppInvite] = useState(!worker);

  const [selectedRoleId, setSelectedRoleId] = useState(
    worker && worker.role?.id
  );

  const [updateWorker] = useMutation(UPDATE_WORKER, updateWorkerAction(form));
  const [createWorker] = useMutation(CREATE_WORKER, createWorkerAction(form));

  const {
    loading: organizationUnitLoading,
    error: organizationUnitError,
    data: organizationUnitData,
  } = useQuery(WORKS_ORGANIZATION_UNIT_QUERY);

  const [sendInvite] = useMutation(SEND_APP_INVITATION_MUTATION, {
    onCompleted(data) {
      const { message } = data?.sendAppInvitation || {};
      if (message) {
        createMessage('success', message);
      }
    },
  });

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(RolesQuery);

  const loading =
    organizationUnitLoading || rolesLoading || integrationConfigLoading;

  const handleChangeHomeUnit = (isChecked, changedIndex) => {
    const allowedUnits = form.getFieldValue('allowedUnits');

    if (isChecked) {
      allowedUnits.forEach((allowedUnit, index) => {
        allowedUnit.isHomeUnit = index === changedIndex;
      });
    } else {
      allowedUnits[changedIndex].isHomeUnit = false;
    }

    form.setFieldsValue({ allowedUnits });
  };

  const isAllowedUnitHomeUnit = (index) => {
    const allowedUnits = form.getFieldValue('allowedUnits');
    return allowedUnits[index].isHomeUnit;
  };

  const validateUniqueAllowedUnit = async (_, value) => {
    if (!value) return;

    const allowedUnits = form.getFieldValue('allowedUnits');
    const unitCount = allowedUnits.filter(
      (allowedUnit) => allowedUnit.unit === value
    ).length;

    if (unitCount > 1) {
      throw new Error('This unit is already in the list.');
    }
  };

  const validateAtLeastOneAllowedUnit = async () => {
    const allowedUnits = form.getFieldValue('allowedUnits');

    if (inputsEnabled && allowedUnits.length === 0) {
      throw new Error('Please add at least one approved unit and role');
    }
  };

  if (organizationUnitError || rolesError) {
    return (
      <ErrorHandler
        error={organizationUnitError || rolesError}
        log="WorkerForm::NO_WORKS_USER_CACHED"
      />
    );
  }

  const isExistingWorker = () => {
    return !!(worker && worker.id);
  };

  const onFinish = (values) => {
    if (
      isExistingWorker() &&
      worker.workforceGroup?.id !== values.workforceGroupId
    ) {
      onFinishModalConfirm(() => submitForm(form.getFieldsValue()));
    } else {
      submitForm(values);
    }
  };

  const submitForm = (values, acknowledgeOverride = false) => {
    setFormSubmitting(true);
    const input = {
      workerInput: {
        roleId: values.roleId,
        isInternal: values.isInternal,
        homeUnitId: getSelectedHomeUnit(values.allowedUnits),
        employeeId: values.employeeId,
        active: values.active,
        workforceGroupId: values.workforceGroupId,
        ftePercentage: values.ftePercentage ?? null,
      },
      worksUserInput: {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phone: values.phone,
      },
      allowLists: expandAllowedUnitsToAllowLists(values.allowedUnits),
    };

    const updateChildren = async (actionType) => {
      return Promise.all([]).then(() => {
        setFormSubmitting(false);

        if (allFormErrors(form).length === 0) {
          createMessage('success', `Staff member successfully ${actionType}`);
          props.setShowDrawer(false);
        }
      });
    };

    if (isExistingWorker()) {
      updateWorker({
        variables: {
          workerId: worker.id,
          workerInput: input.workerInput,
          worksUserInput: input.worksUserInput,
          allowLists: input.allowLists,
          acknowledgeOverride,
        },
      })
        .then(({ data }) => {
          const conflicts = data?.updateWorker?.mappingConflicts;
          if (conflicts?.length) {
            setFormSubmitting(false);
            confirmWorkforceGroupOverride(conflicts, () =>
              submitForm(values, true)
            );
            return;
          }
          return updateChildren('updated');
        })
        .catch((error) => {
          console.error(error);
          setFormSubmitting(false);
        });
    } else {
      createWorker({
        variables: input,
      })
        .then(({ data }) => {
          if (sendAppInvite && data.createWorker.worker) {
            sendInvite({
              variables: {
                workerId: data.createWorker.worker.id,
              },
            });
          }
          if (!data.createWorker.worker) {
            setFormSubmitting(false);
            return;
          }
          return updateChildren('created');
        })
        .catch((error) => {
          console.error(error);
          setFormSubmitting(false);
        });
    }
  };

  const onFinishFailed = ({ errorFields }) => {
    form.scrollToField(errorFields[0].name);
  };

  const roleOptions = !loading
    ? rolesData.roles
        .map((role) => ({
          label: role.abbreviation,
          value: role.id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    : [];

  const organizationUnitOptions = !loading
    ? uniqBy(
        organizationUnitData.organizationUnits
          .concat(worker?.allowedUnits || [])
          .map((unit) => {
            const label = unit.costCenter
              ? `${unit.name} - ${unit.costCenter}`
              : unit.name;

            return {
              label: label,
              value: unit.id,
            };
          })
          .sort((a, b) => a.label.localeCompare(b.label)),
        'value'
      )
    : [];

  const isOrganizationUnitDisabled = () => {
    return false;
  };

  const initialWorkforceGroup = worker?.workforceGroup?.active
    ? worker.workforceGroup.id
    : props.defaultValues?.workforceGroupId || [];

  const initialValues = {
    firstName: worker && worker.worksUser.firstName,
    lastName: worker && worker.worksUser.lastName,
    phone: worker && worker.worksUser.phone,
    email: worker && worker.worksUser.email,
    roleId: worker && worker.role?.id,
    active: worker ? worker.active : true,
    employeeId: worker && worker.employeeId,
    isInternal: worker ? worker.isInternal : true,
    homeUnitId: worker && worker.homeUnit?.id,
    allowedUnits:
      (worker &&
        formatAllowListsToAllowedUnits(
          worker.homeUnit?.id,
          worker?.allowLists
        )) ||
      [],
    workforceGroupId: initialWorkforceGroup,
    ftePercentage: worker?.ftePercentage,
  };

  const drawerTitle = isExistingWorker() ? (
    <div className="sentry-mask">
      {worker.worksUser.name} {inactiveText}
    </div>
  ) : (
    'Add Staff'
  );

  // TODO: Make this an input param that comes from the backend
  const homeUnitEditPermitted = !allowListSyncEnabled;

  // TODO: Make this an input param that comes from the backend
  const allowListEditPermitted = editingAllowListsPermitted;

  return (
    <>
      <Drawer
        size="lg"
        onClose={() => props.setShowDrawer(false)}
        title={drawerTitle}
        open={true}
        footer={
          <>
            <Button
              htmlType="button"
              size="large"
              style={{ marginRight: '1rem' }}
              onClick={() => props.setShowDrawer(false)}
            >
              Back
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              onClick={form.submit}
              loading={formSubmitting}
            >
              {isExistingWorker() ? 'Update Profile' : 'Add Staff'}
            </Button>
          </>
        }
      >
        {loading ? (
          <LoadingState />
        ) : (
          <Form
            className="worker-form-container"
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            initialValues={initialValues}
          >
            <Form.Item name="isInternal" valuePropName="checked" hidden>
              <Checkbox />
            </Form.Item>

            {workerIntegrationEnabled && (
              <Banner style={{ marginBottom: 16 }} type="info">
                Worker successfully synced with {integrationType?.toUpperCase()}
                . Some fields cannot be edited here. To make changes please
                update the Worker profile in {integrationType?.toUpperCase()} or
                contact your Organization Admin.
              </Banner>
            )}

            {worker ? (
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Form.Item name="active" valuePropName="checked">
                    <Checkbox
                      disabled={formSubmitting}
                      onChange={(e) => setInputsEnabled(e.target.checked)}
                    >
                      Account active
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            ) : (
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Form.Item name="active" valuePropName="checked">
                    <Checkbox
                      disabled={formSubmitting}
                      checked={sendAppInvite}
                      onChange={(e) => setSendAppInvite(e.target.checked)}
                    >
                      Send invite upon creation
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'Please enter a first name' },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter first name"
                    disabled={formSubmitting || !inputsEnabled}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Please enter a last name' },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter last name"
                    disabled={formSubmitting || !inputsEnabled}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter an email address',
                    },
                    {
                      type: 'email',
                      message: 'Please enter a valid email address',
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter email address"
                    disabled={formSubmitting || !inputsEnabled}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Employee ID"
                  name="employeeId"
                  rules={[
                    { required: true, message: 'Please enter an Employee ID' },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Enter Employee ID"
                    disabled={formSubmitting || !inputsEnabled}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Form.Item
                  label="Mobile Number"
                  name="phone"
                  rules={[
                    {
                      pattern: /^(\d{3}-){2}\d{4}$/,
                      message: 'Must be in xxx-xxx-xxxx format',
                    },
                  ]}
                >
                  <PhoneInput
                    type="text"
                    size="large"
                    placeholder="Enter mobile number"
                    disabled={formSubmitting || !inputsEnabled}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Full-Time Equivalent (FTE) %"
                  name="ftePercentage"
                  rules={[
                    {
                      type: 'number',
                      min: 0,
                      max: 100,
                      message: 'Must be between 0% and 100%',
                    },
                  ]}
                >
                  <PercentInput
                    size="large"
                    placeholder="Enter FTE"
                    max={100}
                    step={0.01}
                    disabled={
                      formSubmitting || !inputsEnabled || worker?.fteFromUkg
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[8, 8]}>
              <Col span={12}>
                <Form.Item
                  label={
                    <>
                      Role
                      <InfoTooltip
                        size="md"
                        leftOffset="sm"
                        iconColor="elm"
                        title="This is the worker's primary role. You can add additional roles on a per unit basis below."
                      />
                    </>
                  }
                  name="roleId"
                  rules={[
                    {
                      required: true,
                      message: "Please enter the worker's role",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select Role"
                    size="large"
                    disabled={
                      formSubmitting || !inputsEnabled || !homeUnitEditPermitted
                    }
                    getPopupContainer={(trigger) => trigger.parentNode}
                    options={roleOptions}
                    optionFilterProp="label"
                    onChange={(value) => setSelectedRoleId(value)}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <WorkforceGroupSelect
                  disabled={formSubmitting || !inputsEnabled}
                />
              </Col>
            </Row>

            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Form.Item
                  label="Units"
                  name="approvedUnits"
                  colon={false}
                  required
                  style={{ marginBottom: '8px' }}
                  rules={[{ validator: validateAtLeastOneAllowedUnit }]}
                  dependencies={['active', 'allowedUnits']}
                >
                  <Form.List name="allowedUnits">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map(({ key, name }) => {
                          const isUnitDisabled = isOrganizationUnitDisabled(
                            form.getFieldValue(['allowedUnits', name, 'unit'])
                          );

                          return (
                            <div
                              key={key}
                              className="worker-form-allowed-unit-row"
                            >
                              {isUnitDisabled && (
                                <Row gutter={[8, 16]} justify="center">
                                  <Col span={24}>
                                    <Alert
                                      message="You currently do not have access to this unit"
                                      type="info"
                                      showIcon
                                      closable
                                      style={{
                                        marginBottom: 24,
                                        borderColor: Colors.skyLighter,
                                        backgroundColor: Colors.skyHyperLight,
                                      }}
                                    />
                                  </Col>
                                </Row>
                              )}
                              <Row gutter={[8, 16]} justify="center">
                                <Col span={4}>Unit</Col>
                                <Col span={20}>
                                  <Form.Item
                                    required
                                    name={[name, 'unit']}
                                    style={{ marginBottom: 0 }}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Please select a unit',
                                      },
                                      {
                                        validator: validateUniqueAllowedUnit,
                                      },
                                    ]}
                                  >
                                    <Select
                                      showSearch
                                      placeholder="Select Unit"
                                      size="large"
                                      disabled={
                                        formSubmitting ||
                                        !inputsEnabled ||
                                        !isInternal ||
                                        !allowListEditPermitted ||
                                        (!homeUnitEditPermitted &&
                                          isAllowedUnitHomeUnit(name)) ||
                                        isUnitDisabled
                                      }
                                      options={organizationUnitOptions}
                                      optionFilterProp="label"
                                      getPopupContainer={(trigger) =>
                                        trigger.parentNode
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>Roles</Col>
                                <Col span={20}>
                                  <Form.Item
                                    name={[name, 'roles']}
                                    style={{ marginBottom: 0 }}
                                    rules={[
                                      {
                                        required: true,
                                        message:
                                          'Please select at least one role',
                                      },
                                    ]}
                                  >
                                    <Select
                                      mode="multiple"
                                      placeholder="Select Roles"
                                      options={roleOptions}
                                      optionFilterProp="label"
                                      size="large"
                                      disabled={
                                        formSubmitting ||
                                        !inputsEnabled ||
                                        !isInternal ||
                                        !allowListEditPermitted ||
                                        isUnitDisabled
                                      }
                                      getPopupContainer={(trigger) =>
                                        trigger.parentNode
                                      }
                                    ></Select>
                                  </Form.Item>
                                </Col>
                                <Col span={12} offset={4}>
                                  <Form.Item
                                    name={[name, 'isHomeUnit']}
                                    style={{ marginBottom: 0 }}
                                    valuePropName="checked"
                                    onChange={(e) =>
                                      handleChangeHomeUnit(
                                        e.target.checked,
                                        name
                                      )
                                    }
                                  >
                                    <Checkbox
                                      disabled={
                                        formSubmitting ||
                                        !inputsEnabled ||
                                        !isInternal ||
                                        !homeUnitEditPermitted ||
                                        isUnitDisabled ||
                                        fields.length === 1
                                      }
                                    >
                                      Set as Home Unit
                                    </Checkbox>
                                  </Form.Item>
                                </Col>
                                <Col
                                  span={8}
                                  style={{
                                    textAlign: 'right',
                                    alignSelf: 'center',
                                  }}
                                >
                                  {!formSubmitting &&
                                    inputsEnabled &&
                                    isInternal &&
                                    allowListEditPermitted &&
                                    !isAllowedUnitHomeUnit(name) && (
                                      <div
                                        onClick={() => remove(name)}
                                        className="remove-approved-unit-button-container"
                                      >
                                        <Icon
                                          icon="trash"
                                          kind="regular"
                                          size="md"
                                          color="elm"
                                        />
                                        <span> Remove Unit</span>
                                      </div>
                                    )}
                                </Col>
                              </Row>
                            </div>
                          );
                        })}
                        <Form.Item>
                          {inputsEnabled && allowListEditPermitted && (
                            <>
                              <Button
                                type="dashed"
                                onClick={() =>
                                  add({
                                    unit: undefined,
                                    roles: [selectedRoleId],
                                    isHomeUnit: fields.length === 0,
                                  })
                                }
                                disabled={
                                  !isInternal ||
                                  !selectedRoleId ||
                                  formSubmitting
                                }
                                block
                                style={{ width: '25%' }}
                              >
                                <Icon icon="plus" kind="regular" />
                                <span style={{ marginLeft: 8 }}>Unit</span>
                              </Button>
                              {isInternal && !selectedRoleId && (
                                <InfoTooltip
                                  size="md"
                                  leftOffset="md"
                                  iconColor="elm"
                                  title="To add Units you must select a Role."
                                />
                              )}
                              {!isInternal && (
                                <InfoTooltip
                                  size="md"
                                  leftOffset="md"
                                  iconColor="elm"
                                  title="Please contact your Works Admin to add or remove a unit to this profile."
                                />
                              )}
                            </>
                          )}
                        </Form.Item>
                      </>
                    )}
                  </Form.List>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
        <style jsx>{`
          :global(.worker-form-container) :global(.ant-input-number),
          :global(.worker-form-container)
            :global(.ant-form-item-control-input-content),
          :global(.worker-form-container) :global(.ant-picker) {
            width: 100%;
          }
          .remove-approved-unit-button-container {
            cursor: pointer;
          }
          .worker-form-allowed-unit-row {
            padding: 0 0 16px 0;
            border-bottom: 1px solid ${Colors.steel};
            margin: 16px 0 24px 0;
          }
        `}</style>
      </Drawer>
    </>
  );
}

WorkerForm.propTypes = {
  worker: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.shape({
      id: PropTypes.string,
    }),
    baseRate: PropTypes.number,
    ftePercentage: PropTypes.number,
    fteFromUkg: PropTypes.bool,
    active: PropTypes.bool,
    employeeId: PropTypes.string,
    isInternal: PropTypes.bool,
    homeUnit: PropTypes.shape({
      id: PropTypes.string,
    }),
    allowLists: PropTypes.array,
    allowedUnits: PropTypes.array,
    workforceGroup: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      active: PropTypes.bool,
    }),
    worksUser: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
    }),
  }),
  setShowDrawer: PropTypes.func,
  workerIntegrationEnabled: PropTypes.bool,
  allowListSyncEnabled: PropTypes.bool,
  editingAllowListsPermitted: PropTypes.bool,
  workerFound: PropTypes.bool,
  integrationType: PropTypes.string,
  integrationConfigLoading: PropTypes.bool,
  defaultValues: PropTypes.shape({
    workforceGroupId: PropTypes.string,
  }),
};
